const ALIGNMENTS = ['center', 'right', 'justify'] as const;

/**
 * Converts legacy Quill class-based alignment/direction markup
 * (ql-align-*, ql-direction-rtl) to the inline styles the editor now
 * serializes (see registerFormats.ts). Runs before content is seeded into
 * the editor; without it, legacy alignment would render but be invisible
 * to the toolbar state and impossible to change or clear.
 *
 * Guarantees:
 * - No-op guard: content without those classes is returned byte-identical
 *   (no DOMParser round-trip), so unaffected forms never appear dirty.
 * - ql-indent-N classes are intentionally left alone: indent has no
 *   style attributor in Quill and stays class-based (styled by the
 *   consumer-side Quill content CSS).
 * - ql-direction-rtl KEEPS its class alongside the added inline style:
 *   list-marker CSS everywhere keys on the class (see registerFormats).
 * - Plain text is never touched, so gap-question tokens ($$0, $$1, ...)
 *   and bracket answer syntax ([a|(b)]) survive unchanged.
 */
export function normalizeLegacyQuillHtml(html: string): string {
	if (!html) return html;
	if (!html.includes('ql-align-') && !html.includes('ql-direction-rtl')) {
		return html;
	}

	const doc = new DOMParser().parseFromString(html, 'text/html');
	doc.body
		.querySelectorAll('[class*="ql-align-"], [class*="ql-direction-rtl"]')
		.forEach((node) => {
			const el = node as HTMLElement;
			ALIGNMENTS.forEach((align) => {
				if (el.classList.contains(`ql-align-${align}`)) {
					el.style.textAlign = align;
					el.classList.remove(`ql-align-${align}`);
				}
			});
			if (el.classList.contains('ql-direction-rtl')) {
				el.style.direction = 'rtl';
				// Class intentionally kept: marker/indent CSS keys on it.
			}
			if (!el.classList.length) el.removeAttribute('class');
		});
	return doc.body.innerHTML;
}
